import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Form, Spinner, Table } from "react-bootstrap";
import {
  getSertifikatAuditorByLembaga,
  getSertifikatJulehaByLembaga,
  getSertifikatPenyeliaByLembaga,
} from "../../services/api";
import "./LpkDataPelatihanPenyeliaHalal.css";

const TAB_OPTIONS = [
  { key: "penyelia", label: "Pelatihan Penyelia" },
  { key: "auditor", label: "Pelatihan Auditor" },
  { key: "juleha", label: "Pelatihan JPH Lainnya" },
];

const getTabApi = (tabKey) => {
  if (tabKey === "auditor") return getSertifikatAuditorByLembaga;
  if (tabKey === "juleha") return getSertifikatJulehaByLembaga;
  return getSertifikatPenyeliaByLembaga;
};

const buildFileUrl = (fileName) => {
  if (!fileName) return "";
  return `https://lembaga.halal.go.id/files/${encodeURIComponent(fileName)}`;
};

const LpkInquiry = () => {
  const [activeTab, setActiveTab] = useState("penyelia");
  const [selectedAngkatan, setSelectedAngkatan] = useState("");
  // const [angkatanTouched, setAngkatanTouched] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchRows = async () => {
      try {
        setLoading(true);
        setError("");
        const loader = getTabApi(activeTab);
        const response = await loader();
        const data = Array.isArray(response.data?.data) ? response.data.data : [];
        setRows(data);
      } catch (err) {
        setRows([]);
        setError(
          err.response?.data?.message ||
            "Gagal memuat data inquiry dari server.",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchRows();
    // Reset angkatan filter on tab change
    setSelectedAngkatan("");
    // setAngkatanTouched(false);
  }, [activeTab]);

  const angkatanOptions = useMemo(() => {
    return Array.from(
      new Set(
        rows
          .map((item) => String(item?.Angkatan || "").trim())
          .filter((value) => value !== ""),
      ),
    ).sort((a, b) => Number(a) - Number(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchAngkatan =
        !selectedAngkatan || String(row?.Angkatan || "") === selectedAngkatan;

      const q = searchText.trim().toLowerCase();
      const matchSearch =
        !q ||
        String(row?.NIK || "").toLowerCase().includes(q) ||
        String(row?.Nama || "").toLowerCase().includes(q);

      return matchAngkatan && matchSearch;
    });
  }, [rows, selectedAngkatan, searchText]);

  const handleTabChange = (tabKey) => {
    if (tabKey === activeTab) return;
    setActiveTab(tabKey);
    setSelectedAngkatan("");
    setSearchText("");
    // setAngkatanTouched(false);
  };

  const handlePilihAngkatan = (value) => {
    setSelectedAngkatan(value);
  };

  return (
    <div className="lpk-penyelia-page">
      <Card className="lpk-main-card">
        <Card.Header className="lpk-card-header">Inquiry Pelatihan</Card.Header>
        <Card.Body>
          <div className="mb-3 d-flex gap-2 flex-wrap">
            {TAB_OPTIONS.map((tab) => (
              <Button
                key={tab.key}
                type="button"
                variant={activeTab === tab.key ? "primary" : "outline-primary"}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
              </Button>
            ))}
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          <div className="lpk-filter-block">
            <div className="lpk-filter-submit-row">
              <Form.Group className="lpk-form-row" controlId="filterAngkatanInquiry">
                <Form.Label>Angkatan</Form.Label>
                <Form.Select
                  value={selectedAngkatan}
                  onChange={(e) => handlePilihAngkatan(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Semua Angkatan</option>
                  {angkatanOptions.map((angkatan) => (
                    <option key={angkatan} value={angkatan}>
                      {angkatan}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="lpk-search-row">
              <Form.Group className="lpk-form-row lpk-search-input" controlId="searchInquiry">
                <Form.Label>Cari NIK / Nama</Form.Label>
                <Form.Control
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Masukkan NIK atau Nama"
                />
              </Form.Group>
            </div>
          </div>

          <div className="table-responsive mt-3">
            <Table bordered hover size="sm" className="mb-0 lpk-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>NIK</th>
                  <th>Jenis Pelatihan</th>
                  <th>Nama</th>
                  <th>Angkatan</th>
                  <th>Status</th>
                  <th>Sertifikat</th>
                  <th>Sertifikat (Sudah TTE)</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8}>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Memuat data...
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={8}>Tidak ada data ditemukan</td>
                  </tr>
                ) : (
                  filteredRows.map((row, idx) => (
                    <tr key={row?.id || `${row?.NIK || "row"}-${idx}`}>
                      <td>{idx + 1}</td>
                      <td>{row?.NIK || "-"}</td>
                      <td>{row?.jenis_pelatihan || row?.Jenis_Pelatihan || "-"}</td>
                      <td>{row?.Nama || "-"}</td>
                      <td>{row?.Angkatan || "-"}</td>
                      <td>{row?.status || "-"}</td>
                      <td>
                        {row?.FileSertifikat ? (
                          <a
                            href={buildFileUrl(row.FileSertifikat)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {row.FileSertifikat}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {row?.FileSertifikatTTE ? (
                          <a
                            href={buildFileUrl(row.FileSertifikatTTE)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {row.FileSertifikatTTE}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default LpkInquiry;
